/**
 * Optimized HomeFAQ Component
 * Performance improvements:
 * - React.memo for preventing unnecessary re-renders
 * - useMemo for memoization
 * - useCallback for event handlers
 * - Intersection Observer for view tracking
 * - Analytics tracking for views, expand/collapse actions
 * - Enhanced accessibility with ARIA attributes
 */

'use client';

import { memo, useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useIntersectionObserver, useAnalytics } from '@/hooks/usePerformance';
import type { FAQGroup } from './schema';
import "./style.css";

export type { FAQGroup } from './schema';

type HomeFAQProps = {
    faqs: FAQGroup[];
};

// Memoized FAQItem component
const FAQItem = memo(function FAQItem({
    question,
    answer,
    isOpen,
    onClick,
    isLast,
    groupIndex,
    itemIndex,
}: {
    question: string;
    answer: JSX.Element;
    isOpen: boolean;
    onClick: () => void;
    isLast?: boolean;
    groupIndex: number;
    itemIndex: number;
}) {
    const itemRef = useRef<HTMLDivElement>(null);
    const hasTrackedView = useRef(false);
    const { trackEvent } = useAnalytics();

    // Track FAQ item visibility
    const isInView = useIntersectionObserver(itemRef, {
        threshold: 0.5,
        rootMargin: '50px',
    });

    // Track when FAQ item becomes visible (only once)
    useEffect(() => {
        if (isInView && !hasTrackedView.current) {
            trackEvent('faq_item_view', {
                question_text: question,
                group_index: groupIndex,
                item_index: itemIndex,
            });
            hasTrackedView.current = true;
        }
    }, [isInView, question, groupIndex, itemIndex, trackEvent]);

    // Track expand/collapse with analytics
    const handleClick = useCallback(() => {
        trackEvent(isOpen ? 'faq_collapsed' : 'faq_expanded', {
            question_text: question,
            group_index: groupIndex,
            item_index: itemIndex,
        });
        onClick();
    }, [isOpen, question, groupIndex, itemIndex, onClick, trackEvent]);

    // Handle keyboard interaction
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
        }
    }, [handleClick]);

    return (
        <div ref={itemRef} className="faq_item border-b">
            <div
                className="faq_question-wrapper"
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                aria-controls={`faq-answer-${groupIndex}-${itemIndex}`}
                style={{ cursor: "pointer" }}
            >
                <h3 className="text-[1.3rem] font-normal">{question}</h3>
                <div
                    className="toggle_icon-wrapper"
                    style={isOpen ? { backgroundColor: "var(--pink--600)" } : {}}
                    aria-hidden="true"
                >
                    <div className="toggle_icon">
                        <div
                            className="toggle_icon-line-vertical"
                            style={{
                                transform: isOpen
                                    ? "translate3d(0,0,0) scale3d(1,1,1) rotateZ(90deg)"
                                    : "translate3d(0,0,0) scale3d(1,1,1) rotateZ(0deg)",
                                transition: "transform 0.3s cubic-bezier(.77,0,.18,1)",
                            }}
                        ></div>
                        <div
                            className="toggle_icon-line-horizontal"
                            style={isOpen ? { backgroundColor: "#fff" } : {}}
                        ></div>
                    </div>
                </div>
            </div>
            {isOpen && (
                <div
                    id={`faq-answer-${groupIndex}-${itemIndex}`}
                    className={`faq_answer-wrapper${isLast ? " is-last-faq" : ""}`}
                    role="region"
                    aria-labelledby={`faq-question-${groupIndex}-${itemIndex}`}
                >
                    <p className="faq_answer text-gray-600 dark:text-gray-400">{answer}</p>
                </div>
            )}
        </div>
    );
});

function HomeFAQComponent({ faqs }: HomeFAQProps) {
    const [openIdx, setOpenIdx] = useState<string>('');
    const sectionRef = useRef<HTMLDivElement>(null);
    const hasTrackedView = useRef(false);
    const { trackEvent } = useAnalytics();

    // Track section visibility
    const isInView = useIntersectionObserver(sectionRef, {
        threshold: 0.2,
        rootMargin: '50px',
    });

    // Calculate total FAQs
    const totalFAQs = useMemo(() =>
        faqs.reduce((acc, group) => acc + group.items.length, 0),
        [faqs]
    );

    // Track when section becomes visible (only once)
    useEffect(() => {
        if (isInView && !hasTrackedView.current) {
            trackEvent('faq_section_view', {
                total_categories: faqs.length,
                total_faqs: totalFAQs,
            });
            hasTrackedView.current = true;
        }
    }, [isInView, faqs.length, totalFAQs, trackEvent]);

    // Memoized click handler
    const handleFAQClick = useCallback((idx: string) => {
        setOpenIdx(prevIdx => prevIdx === idx ? '' : idx);
    }, []);

    return (
        <section ref={sectionRef} className="section_home-faq">
            <div className="padding-global">
                <div className="container-large">
                    <div className="padding-vertical padding-xhuge">
                        <div className="home-faq_component">
                            <div className="max-width-large align-center">
                                <div className="margin-bottom margin-large">
                                    <h2 className="heading-medium text-[var(--primary--burgundy)] dark:text-[var(--primary--pink)]">
                                        Frequently asked questions (FAQs)
                                    </h2>
                                </div>
                                <div className="faq_component">
                                    {faqs.map((group, groupIdx) => (
                                        <div key={`faq-group-${groupIdx}`}>
                                            {group.category && (
                                                <h3 className="mt-4 font-medium">{group.category}</h3>
                                            )}
                                            {group.items.map((faq, idx) => (
                                                <FAQItem
                                                    key={`faq-${groupIdx}-${idx}`}
                                                    question={faq.question}
                                                    answer={faq.answer}
                                                    isOpen={openIdx === `${idx}-${groupIdx}`}
                                                    onClick={() => handleFAQClick(`${idx}-${groupIdx}`)}
                                                    isLast={idx === group.items.length - 1}
                                                    groupIndex={groupIdx}
                                                    itemIndex={idx}
                                                />
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// Export memoized component
export const HomeFAQ = memo(HomeFAQComponent);
HomeFAQ.displayName = 'HomeFAQ';

// Default export for backward compatibility
export default HomeFAQ;
